import React from 'react';
import { INTERFACE_TEXT } from '../../constants/interfaceText';
import styles from './PauseOverlay.module.css';

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * Modal behaviour shared by the pause and leave dialogs: focus moves to the first
 * button on open, Esc calls `onEscape`, Tab stays inside, focus returns on close.
 */
export function useModalDialog(
  onEscape: () => void
): React.RefObject<HTMLDivElement | null> {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const escapeRef = React.useRef(onEscape);
  escapeRef.current = onEscape;
  React.useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    node?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        escapeRef.current();
        return;
      }
      if (e.key !== 'Tab' || !node) return;
      const items = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)];
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!node.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return (): void => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []);
  return ref;
}

export const PauseOverlay: React.FC<{ onResume: () => void }> = ({
  onResume,
}) => {
  const ref = useModalDialog(onResume);
  return (
    <div className={styles.overlay}>
      <div
        ref={ref}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="pause-heading"
        aria-describedby="pause-body"
      >
        <h3 id="pause-heading" className={styles.heading}>
          {INTERFACE_TEXT.tsPausedHeading}
        </h3>
        <p id="pause-body" className={styles.body}>
          {INTERFACE_TEXT.tsPausedBody}
        </p>
        <button type="button" className={styles.primary} onClick={onResume}>
          {INTERFACE_TEXT.tsResume}
        </button>
      </div>
    </div>
  );
};

export default PauseOverlay;
